import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCommentSms, faEnvelope, faPaperPlane } from '@fortawesome/pro-light-svg-icons';
import { Channel, Inbox, InboxContact } from '@spike/omnichannel-model';
import clsx from 'clsx';
import EmojiPickerButton from 'components/UI/V3/EmojiPickerButton';
import React, { useEffect, useRef, useState } from 'react';
import { inboxForChannel } from '../channelUtils';
import { realEmail } from '../contactEmail';
import styles from './Composer.module.scss';

/** Same cap the legacy /sms composer puts on its textarea, so a typed message stays one SMS. */
const SMS_MAX_LENGTH = 160;

interface ComposerProps {
    contact: InboxContact;
    inboxes: Array<Inbox>;
    sending?: boolean;
    sendError?: string | null;
    defaultChannel?: Channel;
    /** "Reply via" in a thread; "Send via" when starting a conversation. */
    channelLabel?: string;
    /** Resolve/return `false` to keep the draft (the send failed); anything else clears it. */
    onSend: (body: string, inboxId: number) => void | boolean | Promise<void | boolean>;
}

export const Composer: React.FC<ComposerProps> = ({
    contact,
    inboxes,
    sending,
    sendError,
    defaultChannel,
    channelLabel = 'Reply via',
    onSend
}) => {
    const [channel, setChannel] = useState<Channel>(defaultChannel || 'sms');
    const [body, setBody] = useState('');
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const smsInbox = inboxForChannel(inboxes, 'sms');
    const emailInbox = inboxForChannel(inboxes, 'email');
    const selectedInbox = channel === 'sms' ? smsInbox : emailInbox;

    const isSms = channel === 'sms';
    const contactEmail = realEmail(contact.email);
    const smsAvailable = Boolean(contact.phone);
    const emailAvailable = Boolean(contactEmail);
    const channelAvailable = channel === 'sms' ? smsAvailable : emailAvailable;

    useEffect(() => {
        if (
            defaultChannel &&
            ((defaultChannel === 'sms' && smsAvailable) || (defaultChannel === 'email' && emailAvailable))
        ) {
            setChannel(defaultChannel);
        }
    }, [contact.id, defaultChannel, smsAvailable, emailAvailable]);

    useEffect(() => {
        if (!emailAvailable && channel === 'email' && smsAvailable) setChannel('sms');
        if (!smsAvailable && channel === 'sms' && emailAvailable) setChannel('email');
    }, [emailAvailable, smsAvailable, channel]);

    const handleSend = async () => {
        if (!body.trim() || !channelAvailable || !selectedInbox || sending) return;
        const sent = await onSend(body, selectedInbox.id);
        if (sent === false) return;
        setBody('');
        textareaRef.current?.focus();
    };

    /**
     * Inserts where the caret is, not at the end — the legacy /sms picker appends and it shows.
     *
     * `maxLength` only constrains what the user types, not what code writes, so the legacy picker
     * can push a message past its own cap (159 characters + an emoji lands on 161) and that is the
     * one path that reaches its broken over-the-limit counter. Refuse the insert instead.
     */
    const handleEmoji = (emoji: string) => {
        const textarea = textareaRef.current;
        const caret = textarea ? textarea.selectionStart : body.length;
        const next = `${body.slice(0, caret)}${emoji}${body.slice(caret)}`;

        if (isSms && next.length > SMS_MAX_LENGTH) return;

        setBody(next);
        window.requestAnimationFrame(() => {
            if (!textarea) return;
            const position = caret + emoji.length;
            textarea.focus();
            textarea.setSelectionRange(position, position);
        });
    };

    const destination = channel === 'sms' ? contact.phone : contactEmail;
    // The counter and the cap both measure UTF-16 code units, so they agree: the field stops taking
    // input exactly when the counter reads 160/160. An emoji is a surrogate pair and costs 2 (4 with
    // a skin tone), which is how the legacy /sms composer has always counted too.
    const emojiFits = body.length < SMS_MAX_LENGTH;

    return (
        <div className={styles.root}>
            <div className={styles.channelRow}>
                <span className={styles.replyVia}>{channelLabel}</span>

                <button
                    type="button"
                    disabled={!smsAvailable}
                    className={clsx(styles.channelOption, channel === 'sms' && styles.channelActive)}
                    onClick={() => setChannel('sms')}
                >
                    <FontAwesomeIcon icon={faCommentSms} /> SMS
                </button>

                <button
                    type="button"
                    disabled={!emailAvailable}
                    className={clsx(styles.channelOption, channel === 'email' && styles.channelActive)}
                    onClick={() => setChannel('email')}
                >
                    <FontAwesomeIcon icon={faEnvelope} /> Email
                </button>

                {selectedInbox && destination && (
                    <span className={styles.destination}>
                        from <strong>{selectedInbox.fromIdentity}</strong> → <strong>{destination}</strong>
                    </span>
                )}
            </div>

            <div className={styles.inputRow}>
                <textarea
                    ref={textareaRef}
                    rows={1}
                    value={body}
                    placeholder={channel === 'sms' ? 'Type an SMS…' : 'Write an email reply…'}
                    className={styles.input}
                    disabled={sending || !channelAvailable}
                    maxLength={isSms ? SMS_MAX_LENGTH : undefined}
                    onChange={event => setBody(event.target.value)}
                    onKeyDown={event => {
                        if (event.key === 'Enter' && !event.shiftKey) {
                            event.preventDefault();
                            handleSend();
                        }
                    }}
                />

                <EmojiPickerButton
                    className={clsx(styles.emoji, isSms && !emojiFits && styles.emojiFull)}
                    ariaLabel="Insert emoji"
                    side="top"
                    onSelect={handleEmoji}
                />

                <button
                    type="button"
                    className={styles.send}
                    disabled={!body.trim() || !channelAvailable || !selectedInbox || sending}
                    onClick={handleSend}
                    aria-label="Send message"
                >
                    <FontAwesomeIcon icon={faPaperPlane} />
                </button>
            </div>

            {isSms && (
                <p className={styles.counter}>
                    {body.length}/{SMS_MAX_LENGTH}
                </p>
            )}

            {!smsAvailable && !emailAvailable && (
                <p className={styles.noChannel}>
                    Add a phone number or an email address to this client to message them.
                </p>
            )}

            {sendError && <p className={styles.sendError}>{sendError}</p>}
        </div>
    );
};

export default Composer;
